import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';
import SearchableSelect from './SearchableSelect';

/**
 * ContactEnrichPanel — provider-agnostic enrichment modal for Lead contact
 * details. The same component drives both Lusha and RocketReach (and any
 * future enrichment provider), branching only on the `provider` prop.
 *
 * Two search modes:
 *   1. By contact name      — single-best-match endpoint
 *   2. By role / seniority  — prospecting-style multi-result search
 *
 * Apply path: when the user clicks "Apply to Lead" on a result, we run a
 * pre-flight diff. If overwriting any populated lead field (contact_name /
 * email / phone), an OverwriteConfirmDialog opens with a side-by-side
 * comparison. The user keeps OR replaces each field independently.
 *
 * Props:
 *   open       — whether the modal is rendered
 *   onClose    — () => void
 *   lead       — current form values { company_name, contact_name, ... }
 *   onApply    — (patch) => void; called with the partial Lead to merge
 *   provider   — 'lusha' | 'rocketreach' (default 'lusha')
 */

// Per-provider config — endpoints + branding. Adding a third provider is a
// 6-line addition here; the rest of the component is provider-blind.
const PROVIDERS = {
  lusha: {
    label: 'Lusha',
    docHelp: 'app.lusha.com → Settings → API',
    keyPlaceholder: 'Lusha API key',
    settingsPath:  '/api/integrations/lusha/config',
    enrichPath:    '/api/integrations/lusha/enrich-person',
    searchPath:    '/api/integrations/lusha/search-contacts',
    supportsRole:  true,
  },
  rocketreach: {
    label: 'RocketReach',
    docHelp: 'rocketreach.co → Settings → API',
    keyPlaceholder: 'RocketReach API key',
    settingsPath:  '/api/integrations/rocketreach/config',
    enrichPath:    '/api/integrations/rocketreach/enrich-person',
    searchPath:    '/api/integrations/rocketreach/search-contacts',
    supportsRole:  true,
  },
};

const SENIORITY_OPTIONS = [
  { value: 'executive', label: 'Executive (CEO / Founder / President)' },
  { value: 'cxo',        label: 'C-Suite (CXO)' },
  { value: 'vp',         label: 'VP' },
  { value: 'director',   label: 'Director' },
  { value: 'manager',    label: 'Manager' },
  { value: 'senior',     label: 'Senior IC' },
  { value: 'entry',      label: 'Entry / IC' },
];

const DEPARTMENT_OPTIONS = [
  { value: 'sales',       label: 'Sales' },
  { value: 'marketing',   label: 'Marketing' },
  { value: 'engineering', label: 'Engineering / IT' },
  { value: 'finance',     label: 'Finance' },
  { value: 'operations',  label: 'Operations' },
  { value: 'hr',          label: 'HR' },
  { value: 'legal',       label: 'Legal' },
  { value: 'product',     label: 'Product' },
];

export default function ContactEnrichPanel({ open, onClose, lead, onApply, provider = 'lusha' }) {
  const cfg = PROVIDERS[provider] || PROVIDERS.lusha;
  const [mode, setMode] = useState('byName');     // 'byName' | 'byRole'
  const [config, setConfig] = useState(null);     // { api_key_set, enabled }
  // Overwrite confirmation modal — opens when Apply would clobber existing
  // contact_name/email/phone values. Holds the candidate contact + a per-
  // field decision map { contact_name: 'replace'|'keep', ... }.
  const [confirmCandidate, setConfirmCandidate] = useState(null);

  // byName inputs
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // byRole inputs
  const [seniority, setSeniority] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState('');     // comma-separated free text

  // Shared
  const [companyName, setCompanyName] = useState('');
  const [companyDomain, setCompanyDomain] = useState('');

  // Results
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);   // array of normalised contacts
  const [error, setError] = useState(null);

  // Settings (config + key entry for admins)
  const [keyInput, setKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);

  // Prefill from the lead on open
  useEffect(() => {
    if (!open) return;
    setCompanyName(lead?.company_name || '');
    setCompanyDomain('');
    // If the user already typed a contact name on the lead, seed the by-name fields
    const parts = (lead?.contact_name || '').trim().split(/\s+/);
    setFirstName(parts[0] || '');
    setLastName(parts.slice(1).join(' ') || '');
    setSeniority([]);
    setDepartments([]);
    setJobTitles('');
    setResults([]);
    setError(null);
    // Load config to know whether to show the API-key form
    (async () => {
      try {
        const r = await apiFetch(cfg.settingsPath);
        const d = await r.json().catch(() => ({}));
        if (r.ok) setConfig(d.config || null);
      } catch (_) { setConfig(null); }
    })();
  }, [open, lead]);

  async function saveKey() {
    if (!keyInput.trim()) { toast.error('Paste the API key first'); return; }
    setSavingKey(true);
    try {
      const r = await apiFetch(cfg.settingsPath, {
        method: 'PUT', body: JSON.stringify({ api_key: keyInput.trim(), enabled: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Save failed'); return; }
      setConfig(d.config || null);
      setKeyInput('');
      toast.success(`${cfg.label} API key saved`);
    } finally { setSavingKey(false); }
  }

  async function search() {
    if (!companyName.trim() && !companyDomain.trim()) {
      setError('Enter the company name or domain'); return;
    }
    setError(null);
    setResults([]);
    setSearching(true);
    try {
      if (mode === 'byName') {
        // Single-contact lookup. Person v2 needs first+last+company OR email/linkedinUrl.
        if (!firstName.trim() || !lastName.trim()) {
          setError('Enter the contact first and last name'); setSearching(false); return;
        }
        const body = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          ...(companyDomain ? { companyDomain: companyDomain.trim() } : {}),
          ...(companyName && !companyDomain ? { companyName: companyName.trim() } : {}),
        };
        const r = await apiFetch(cfg.enrichPath, {
          method: 'POST', body: JSON.stringify(body),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setError(d.error || 'Lookup failed'); return; }
        if (!d.contact) { setError(`${cfg.label} could not find a match`); return; }
        // Attach the raw envelope to the result so a debug fallback can
        // display it when normalisation produced empty extracted fields.
        const merged = d.raw ? { ...d.contact, _raw: d.raw } : d.contact;
        setResults([merged]);
      } else {
        // Prospecting search — list of candidates by role.
        const titles = jobTitles.split(',').map(s => s.trim()).filter(Boolean);
        const body = {
          ...(companyDomain ? { companyDomain: companyDomain.trim() } : {}),
          ...(companyName && !companyDomain ? { companyName: companyName.trim() } : {}),
          seniority,
          departments,
          jobTitles: titles,
          page: 0, size: 25,
        };
        if (!seniority.length && !departments.length && !titles.length) {
          setError('Pick at least one role filter (seniority, department, or job title)');
          return;
        }
        const r = await apiFetch(cfg.searchPath, {
          method: 'POST', body: JSON.stringify(body),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setError(d.error || 'Search failed'); return; }
        if (!d.contacts || !d.contacts.length) { setError('No matching contacts found'); return; }
        setResults(d.contacts);
      }
    } finally { setSearching(false); }
  }

  // Apply a result to the parent Lead form.
  //
  // If applying would clobber an already-populated contact_name / email /
  // phone, we open the OverwriteConfirmDialog with a side-by-side diff so
  // the user can keep or replace each conflicting field independently. If
  // there's no conflict, we apply silently as before.
  function apply(contact) {
    // Decorate the contact with derived fields so buildPatch can pick them
    // up the same way as the primitive ones.
    //   job_title = "Designation - Departments" (e.g. "VP of Sales - Sales")
    // The Lead form stores this as a single column; the user can still
    // hand-edit it after applying.
    const dept = (contact.department || '').trim();
    const designation = (contact.contact_designation || '').trim();
    const jobTitleCombined = designation
      ? (dept ? `${designation} - ${dept}` : designation)
      : null;
    const decorated = { ...contact, job_title: jobTitleCombined };

    const conflicts = computeConflicts(lead, decorated);
    if (conflicts.length === 0) {
      const patch = buildPatch(lead, decorated, {});
      onApply(patch);
      const n = Object.keys(patch).length;
      toast.success(`Applied ${n} field${n === 1 ? '' : 's'}`);
      onClose();
      return;
    }
    const decisions = {};
    for (const c of conflicts) decisions[c.field] = 'replace';
    setConfirmCandidate({ contact: decorated, conflicts, decisions });
  }

  function commitWithDecisions(decisions) {
    const { contact } = confirmCandidate;
    const patch = buildPatch(lead, contact, decisions);
    onApply(patch);
    const n = Object.keys(patch).length;
    toast.success(`Applied ${n} field${n === 1 ? '' : 's'}`);
    setConfirmCandidate(null);
    onClose();
  }

  if (!open) return null;
  const needsKey = config && !config.api_key_set;

  return createPortal(
    <div style={backdrop} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Check {cfg.label}</h2>
          <button onClick={onClose} style={iconBtn} aria-label="Close">×</button>
        </div>

        {/* Setup banner when API key is missing */}
        {needsKey && (
          <div style={banner}>
            <div style={{ marginBottom: 8 }}>
              <strong style={{ color: '#F59E0B' }}>API key not configured.</strong>{' '}
              Paste your {cfg.label} API key — find it at <em>{cfg.docHelp}</em>.
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="password" value={keyInput} onChange={e => setKeyInput(e.target.value)}
                placeholder={cfg.keyPlaceholder} style={{ ...inp, flex: 1 }} />
              <button onClick={saveKey} disabled={savingKey} style={btnPrimary}>{savingKey ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        )}

        {/* Overwrite confirmation modal — shown when Apply would clobber data */}
        {confirmCandidate && (
          <OverwriteConfirmDialog
            lead={lead}
            contact={confirmCandidate.contact}
            conflicts={confirmCandidate.conflicts}
            providerLabel={cfg.label}
            onCancel={() => setConfirmCandidate(null)}
            onConfirm={commitWithDecisions}
          />
        )}

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <ModeBtn active={mode === 'byName'} onClick={() => setMode('byName')}>By contact name</ModeBtn>
          <ModeBtn active={mode === 'byRole'} onClick={() => setMode('byRole')}>By role / seniority</ModeBtn>
        </div>

        {/* Shared inputs */}
        <div style={grid2}>
          <Field label="Company name">
            <input value={companyName} onChange={e => setCompanyName(e.target.value)} style={inp} placeholder="Acme Corp" />
          </Field>
          <Field label="Company domain (preferred)">
            <input value={companyDomain} onChange={e => setCompanyDomain(e.target.value)} style={inp} placeholder="acme.com" />
          </Field>
        </div>

        {/* Mode-specific inputs */}
        {mode === 'byName' ? (
          <div style={grid2}>
            <Field label="First name">
              <input value={firstName} onChange={e => setFirstName(e.target.value)} style={inp} placeholder="Jane" />
            </Field>
            <Field label="Last name">
              <input value={lastName} onChange={e => setLastName(e.target.value)} style={inp} placeholder="Doe" />
            </Field>
          </div>
        ) : (
          <div style={grid2}>
            <Field label="Seniority (one or more)">
              <SearchableMulti value={seniority} onChange={setSeniority}
                options={SENIORITY_OPTIONS} placeholder="Pick seniority levels…" />
            </Field>
            <Field label="Department (one or more)">
              <SearchableMulti value={departments} onChange={setDepartments}
                options={DEPARTMENT_OPTIONS} placeholder="Pick departments…" />
            </Field>
            <Field label="Job titles (free text, comma-separated)" wide>
              <input value={jobTitles} onChange={e => setJobTitles(e.target.value)}
                style={inp} placeholder="VP Sales, Head of IT, CTO" />
            </Field>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={search} disabled={searching || needsKey} style={btnPrimary}>
            {searching ? 'Searching…' : (mode === 'byName' ? 'Look up contact' : 'Search contacts')}
          </button>
        </div>

        {error && <div style={errorBox}>{error}</div>}

        {/* Results */}
        {results.length > 0 && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11.5, color: '#94A3B8', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
              {results.length} result{results.length === 1 ? '' : 's'}
            </div>
            {results.map((c, i) => (
              <ResultCard key={c.lusha_contact_id || c.rocketreach_id || `${c.full_name}-${i}`}
                contact={c} lead={lead} onApply={() => apply(c)} />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ───────────────────────────────────────────────────────────────────
function ResultCard({ contact, lead, onApply }) {
  const [showRaw, setShowRaw] = useState(false);
  const willSetName = !lead?.contact_name && contact.full_name;
  const willSetEmail = !!contact.contact_email;
  const willSetPhone = !!contact.contact_phone;
  // If normalisation didn't extract any of the three core fields, treat
  // this as a debug case — show the raw envelope so the user can see what
  // Lusha actually returned and we can adjust the field mapping.
  const extractionFailed = !contact.full_name && !contact.contact_email && !contact.contact_phone;

  return (
    <div style={resultCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#E5E7EB' }}>
            {contact.full_name || '(no name extracted)'}
            {contact.contact_designation && (
              <span style={{ marginLeft: 8, color: '#94A3B8', fontSize: 12, fontWeight: 400 }}>
                {contact.contact_designation}
              </span>
            )}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#94A3B8' }}>
            {contact.company_name || '—'}
            {contact.industry && ` · ${contact.industry}`}
            {contact.location_city && ` · ${contact.location_city}${contact.location_country ? ', ' + contact.location_country : ''}`}
          </div>
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {contact.contact_email && <Chip ok>📧 {contact.contact_email}</Chip>}
            {contact.contact_phone && <Chip ok>📞 {contact.contact_phone}</Chip>}
            {contact.linkedin_url && <Chip>in {shortLI(contact.linkedin_url)}</Chip>}
          </div>
        </div>
        <button onClick={onApply} disabled={extractionFailed} style={extractionFailed ? btnDisabled : btnPrimary}
          title={extractionFailed ? 'Nothing to apply — see raw response below' : 'Apply to Lead'}>
          Apply to Lead
        </button>
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: '#64748B' }}>
        Will update:{' '}
        {willSetName && <Chip>contact name</Chip>}{' '}
        {willSetEmail && <Chip>email</Chip>}{' '}
        {willSetPhone && <Chip>phone</Chip>}
        {!willSetName && !willSetEmail && !willSetPhone && '—'}
      </div>

      {/* Debug fallback — surface the raw Lusha response when the normaliser
          produced nothing extractable. Lets the admin see exactly what
          Lusha sent so we can adjust field mapping. */}
      {extractionFailed && contact._raw && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 8,
                      background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.40)', color: '#FCD34D' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <strong style={{ fontSize: 12 }}>Lusha returned data but no name/email/phone was extracted.</strong>
            <button onClick={() => setShowRaw(s => !s)} style={btnLink}>{showRaw ? 'Hide raw' : 'Show raw'}</button>
          </div>
          <div style={{ fontSize: 11, color: '#FCD34D' }}>
            This usually means Lusha's response shape changed. The raw payload is below — share it
            so we can update the field mapping.
          </div>
          {showRaw && (
            <pre style={{
              marginTop: 8, padding: 10, borderRadius: 6, maxHeight: 280, overflow: 'auto',
              background: 'rgba(0,0,0,0.40)', color: '#E5E7EB', fontSize: 11.5, lineHeight: 1.45,
            }}>{JSON.stringify(contact._raw, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function shortLI(url) { return String(url).replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, '').replace(/\/$/, ''); }

// ───────────────────────────────────────────────────────────────────
// Apply-time conflict resolution.
//
// Mapping table: Lead field ↔ enriched field. Drives both the diff
// detection AND the patch construction so the rules stay in lockstep.
const APPLY_FIELDS = [
  { field: 'contact_name',  from: 'full_name',     label: 'Contact name'  },
  { field: 'contact_email', from: 'contact_email', label: 'Contact email' },
  { field: 'contact_phone', from: 'contact_phone', label: 'Contact phone' },
  // job_title carries the combined "Designation - Departments" string that
  // ContactEnrichPanel.apply() composes from contact_designation + department.
  { field: 'job_title',     from: 'job_title',     label: 'Job title'     },
  { field: 'linkedin_url',  from: 'linkedin_url',  label: 'LinkedIn URL'  },
];

/** A "conflict" is a field where the lead already has a non-empty value
 *  AND the enriched contact has a different non-empty value. We only
 *  prompt for conflicts on the three core fields the user cares about
 *  (name, email, phone) — designation/linkedin merge silently when the
 *  lead's value is empty (still skipped if not empty, no prompt). */
function computeConflicts(lead, contact) {
  const out = [];
  for (const f of APPLY_FIELDS.slice(0, 3)) {     // name + email + phone only
    const leadVal = (lead?.[f.field] || '').trim();
    const newVal  = (contact?.[f.from] || '').trim();
    if (leadVal && newVal && leadVal !== newVal) {
      out.push({ field: f.field, label: f.label, current: leadVal, incoming: newVal });
    }
  }
  return out;
}

/** Build the partial Lead patch given conflict decisions.
 *  - For non-conflicting fields, default behaviour applies:
 *      contact_name        → only set if lead is empty
 *      contact_email/phone → always set when enriched value exists
 *      designation/linkedin → only set if lead is empty
 *  - For conflicting fields, the user's per-field decision wins:
 *      'keep' → omit (don't touch)
 *      'replace' → set to enriched value
 */
function buildPatch(lead, contact, decisions) {
  const patch = {};
  for (const f of APPLY_FIELDS) {
    const leadVal = (lead?.[f.field] || '').trim();
    const newVal  = (contact?.[f.from] || '').trim();
    if (!newVal) continue;
    if (leadVal && leadVal !== newVal) {
      // Conflict — only the three core fields produce a decision; for
      // designation / linkedin we never overwrite without an explicit
      // user gesture, so just keep them in conflict cases.
      const decision = decisions[f.field] || 'keep';
      if (decision === 'replace') patch[f.field] = newVal;
    } else if (!leadVal) {
      patch[f.field] = newVal;
    } else {
      // Same value already — no-op.
    }
  }
  return patch;
}

// ───────────────────────────────────────────────────────────────────
// OverwriteConfirmDialog — modal with side-by-side diff for fields that
// would be overwritten. Each conflict row offers Keep / Replace toggle.
// Renders into document.body so it floats above the parent panel cleanly.
function OverwriteConfirmDialog({ lead, contact, conflicts, providerLabel, onCancel, onConfirm }) {
  const [decisions, setDecisions] = useState(() => {
    const d = {}; for (const c of conflicts) d[c.field] = 'replace'; return d;
  });
  function set(field, val) { setDecisions(d => ({ ...d, [field]: val })); }

  return createPortal(
    <div style={confirmBackdrop} onClick={onCancel}>
      <div style={confirmBody} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Overwrite existing values?</h2>
          <button onClick={onCancel} style={iconBtn} aria-label="Close">×</button>
        </div>
        <p style={{ marginTop: 0, marginBottom: 14, fontSize: 12.5, color: '#94A3B8' }}>
          Some Lead fields are already populated. Pick which ones {providerLabel} should replace.
          Choose <strong>Keep</strong> to protect the current value.
        </p>

        {/* Diff table — three columns: field name / current / incoming + keep|replace toggle */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1.5fr 1fr', gap: 0, fontSize: 12.5 }}>
          <DiffHead>Field</DiffHead>
          <DiffHead>Current</DiffHead>
          <DiffHead>{providerLabel}</DiffHead>
          <DiffHead>Action</DiffHead>
          {conflicts.map(c => {
            const replace = decisions[c.field] === 'replace';
            return (
              <React.Fragment key={c.field}>
                <DiffCell><strong style={{ color: '#E5E7EB' }}>{c.label}</strong></DiffCell>
                <DiffCell strike={replace}>
                  <span style={{ color: '#CBD5E1' }}>{c.current}</span>
                </DiffCell>
                <DiffCell>
                  <span style={{ color: replace ? '#10B981' : '#94A3B8' }}>{c.incoming}</span>
                </DiffCell>
                <DiffCell>
                  <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <SegBtn active={!replace} onClick={() => set(c.field, 'keep')}>Keep</SegBtn>
                    <SegBtn active={replace}  onClick={() => set(c.field, 'replace')} accent>Replace</SegBtn>
                  </div>
                </DiffCell>
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <div style={{ fontSize: 11.5, color: '#64748B' }}>
            Empty fields will be filled silently — no prompt for those.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} style={btnSecondary}>Cancel</button>
            <button onClick={() => onConfirm(decisions)} style={btnPrimary}>Apply selected</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DiffHead({ children }) {
  return <div style={{ padding: '8px 10px', fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#94A3B8', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>{children}</div>;
}
function DiffCell({ children, strike }) {
  return <div style={{ padding: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', textDecoration: strike ? 'line-through' : 'none', wordBreak: 'break-word' }}>{children}</div>;
}
function SegBtn({ active, onClick, children, accent }) {
  return (
    <button onClick={onClick} style={{
      padding: '5px 10px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
      background: active ? (accent ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.10)') : 'transparent',
      color: active ? (accent ? '#10B981' : '#E5E7EB') : '#94A3B8',
    }}>{children}</button>
  );
}

// ───────────────────────────────────────────────────────────────────
// Lightweight multi-select chip control for seniority / departments.
function SearchableMulti({ value, onChange, options, placeholder }) {
  const remaining = useMemo(() => options.filter(o => !value.includes(o.value)), [options, value]);
  function add(v) { onChange([...value, v]); }
  function remove(v) { onChange(value.filter(x => x !== v)); }
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
        {value.map(v => {
          const o = options.find(x => x.value === v);
          return (
            <span key={v} style={chip}>
              {o ? o.label : v}
              <button onClick={() => remove(v)} style={chipX}>×</button>
            </span>
          );
        })}
      </div>
      {remaining.length > 0 && (
        <SearchableSelect value="" onChange={add} options={remaining}
          placeholder={placeholder} />
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
      border: '1px solid ' + (active ? 'rgba(0,224,255,0.55)' : 'rgba(255,255,255,0.10)'),
      background: active ? 'rgba(0,224,255,0.14)' : 'rgba(255,255,255,0.04)',
      color: active ? '#6FE6FF' : '#E5E7EB',
    }}>{children}</button>
  );
}

function Field({ label, children, wide }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <span style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

function Chip({ children, ok }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999,
      background: ok ? 'rgba(16,185,129,0.10)' : 'rgba(255,255,255,0.06)',
      border: `1px solid ${ok ? 'rgba(16,185,129,0.40)' : 'rgba(255,255,255,0.10)'}`,
      color: ok ? '#10B981' : '#CBD5E1',
      fontSize: 11, fontWeight: 600,
    }}>{children}</span>
  );
}

// ─── Styles ────────────────────────────────────────────────────────
const backdrop = {
  position: 'fixed', inset: 0, zIndex: 9000,
  background: 'rgba(2, 6, 23, 0.70)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const panel = {
  width: 'min(720px, 100%)', maxHeight: '92vh', overflowY: 'auto',
  background: '#0B1020', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, padding: 18,
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const banner = {
  marginBottom: 14, padding: 12, borderRadius: 10,
  background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.40)',
  color: '#FCD34D', fontSize: 12.5,
};
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 };
const inp = {
  padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 13, outline: 'none', width: '100%',
};
const btnPrimary = {
  padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
  background: 'linear-gradient(135deg, #00E0FF, #3B8FF4)', color: '#fff',
};
const btnDisabled = {
  padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'not-allowed',
  background: 'rgba(255,255,255,0.06)', color: '#64748B',
};
const btnLink = {
  padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
  background: 'transparent', color: '#FCD34D', textDecoration: 'underline',
};
const btnSecondary = {
  padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer',
};
const confirmBackdrop = {
  position: 'fixed', inset: 0, zIndex: 9500,
  background: 'rgba(2, 6, 23, 0.78)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const confirmBody = {
  width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto',
  background: '#0B1020', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, padding: 18,
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const iconBtn = {
  width: 28, height: 28, borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)',
  color: '#94A3B8', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0,
};
const errorBox = {
  marginTop: 10, padding: '8px 12px', borderRadius: 8,
  background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.40)',
  color: '#FB7185', fontSize: 12.5,
};
const resultCard = {
  padding: 12, borderRadius: 10,
  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
};
const chip = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 999,
  background: 'rgba(0,224,255,0.10)', border: '1px solid rgba(0,224,255,0.40)', color: '#6FE6FF', fontSize: 11, fontWeight: 600,
};
const chipX = { marginLeft: 2, background: 'transparent', border: 'none', color: '#6FE6FF', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 };
